/**
 * compareTransform.js
 *
 * Utility module for comparison chart functionality.
 * Follows ChartIQ pattern where:
 * - Data is NOT pre-transformed to percentages
 * - Transforms happen at render time using a dynamic baseline
 * - Baseline = close price of first VISIBLE bar
 * - Baseline updates on pan/zoom
 */

// ============================================================================
// CORE TRANSFORM FUNCTIONS (ChartIQ-style)
// ============================================================================

/**
 * Get the baseline price for percentage calculations.
 * The baseline is the close price of the first visible bar.
 * @param {Array} data - Chart data array
 * @param {number} firstVisibleIndex - Index of first visible bar
 * @param {string} field - Field to use for baseline (default: 'close')
 * @returns {number|null} - Baseline price or null if not found
 */
export function getCompareBaseline(
  data,
  firstVisibleIndex = 0,
  field = "close",
) {
  if (!data || data.length === 0) return null;

  // Clamp index to valid range
  const index = Math.max(0, Math.min(firstVisibleIndex, data.length - 1));

  // Find first non-null value starting from the visible index
  for (let i = index; i < data.length; i++) {
    const value = data[i]?.[field];
    if (value !== null && value !== undefined && value !== 0) {
      return value;
    }
  }

  return null;
}

/**
 * Transform a price value to percentage relative to baseline.
 * This is the core transform function used for Y-axis positioning.
 * @param {number} price - The price to transform
 * @param {number} baseline - The baseline price (0% reference point)
 * @returns {number|null} - Percentage value or null if invalid
 */
export function priceToPercent(price, baseline) {
  if (price === null || price === undefined) return null;
  if (!baseline || baseline === 0) return null;

  return ((price - baseline) / baseline) * 100;
}

/**
 * Transform a percentage back to price value.
 * Used for tooltip display to show real prices.
 * @param {number} percent - The percentage value
 * @param {number} baseline - The baseline price
 * @returns {number|null} - Price value or null if invalid
 */
export function percentToPrice(percent, baseline) {
  if (percent === null || percent === undefined) return null;
  if (!baseline || baseline === 0) return null;

  return baseline * (1 + percent / 100);
}

/**
 * Format a percentage value for display.
 * @param {number} percent - The percentage value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted percentage string (e.g., "+5.23%", "-3.10%")
 */
export function formatPercent(percent, decimals = 2) {
  if (percent === null || percent === undefined || !isFinite(percent))
    return "";

  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent.toFixed(decimals)}%`;
}

// ============================================================================
// DATA ALIGNMENT FUNCTIONS
// ============================================================================

/**
 * Create a time-indexed map from bar data for fast lookup
 * @param {Array} data - Bar data array
 * @returns {Map} - Map of timestamp -> bar
 */
function createTimeIndex(data) {
  const index = new Map();
  for (const bar of data) {
    if (bar?.date) {
      const timestamp = bar.date.getTime();
      index.set(timestamp, bar);
    }
  }
  return index;
}

/**
 * Align comparison data to main chart's time index.
 * Stores RAW close prices, not percentages.
 * @param {Array} mainData - Main chart data
 * @param {Array} comparisonData - Comparison symbol data
 * @returns {Array} - Aligned comparison data with raw close prices
 */
export function alignToMainTimeline(mainData, comparisonData) {
  if (!mainData || !comparisonData) return [];

  const comparisonIndex = createTimeIndex(comparisonData);
  const aligned = [];

  for (const mainBar of mainData) {
    const timestamp = mainBar.date.getTime();
    const compBar = comparisonIndex.get(timestamp);

    if (compBar) {
      aligned.push({
        date: mainBar.date,
        close: compBar.close,
        open: compBar.open,
        high: compBar.high,
        low: compBar.low,
        volume: compBar.volume,
      });
    } else {
      // No matching bar - use null to indicate gap
      aligned.push({
        date: mainBar.date,
        close: null,
        open: null,
        high: null,
        low: null,
        volume: null,
      });
    }
  }

  return aligned;
}

/**
 * Convert API bars to internal format
 * @param {Array} apiBars - Bars from API (may have T, O, H, L, C, V format)
 * @returns {Array} - Bars with date, open, high, low, close, volume format
 */
function formatApiBars(apiBars) {
  if (!apiBars || apiBars.length === 0) return [];

  return apiBars.map((bar) => {
    // Check if already in internal format (has date property)
    if (bar.date) return bar;

    // Convert from API format (T is Unix timestamp)
    return {
      date: new Date(bar.T * 1000),
      open: bar.O,
      high: bar.H,
      low: bar.L,
      close: bar.C,
      volume: bar.V,
    };
  });
}

// ============================================================================
// DATA MERGE FUNCTIONS
// ============================================================================

/**
 * Merge single comparison symbol's RAW close prices into main chart data.
 * Does NOT calculate percentages - that happens at render time.
 * @param {Array} mainData - Main chart data
 * @param {Array} comparisonBars - Raw comparison symbol bars from API
 * @param {string} symbol - Symbol name (used as data key)
 * @returns {Array} - Main data with comparison symbol's close prices merged
 */
export function mergeComparisonSymbol(mainData, comparisonBars, symbol) {
  if (!mainData || mainData.length === 0) return mainData;
  if (!comparisonBars || comparisonBars.length === 0) return mainData;

  // Convert API bars to internal format
  const formattedBars = formatApiBars(comparisonBars);

  // Align to main timeline
  const aligned = alignToMainTimeline(mainData, formattedBars);

  // Create time index for fast lookup - store RAW close prices
  const closeIndex = new Map();
  for (let i = 0; i < aligned.length; i++) {
    const timestamp = aligned[i].date.getTime();
    closeIndex.set(timestamp, aligned[i].close); // Raw close, not percentage
  }

  // Merge into main data - store raw close price
  const dataKey = `compare_${symbol}_close`;
  return mainData.map((bar) => {
    const timestamp = bar.date.getTime();
    const closePrice = closeIndex.get(timestamp);

    return {
      ...bar,
      [dataKey]: closePrice !== undefined ? closePrice : null,
    };
  });
}

/**
 * Merge all comparison symbols' RAW close prices into main chart data.
 * Does NOT calculate percentages - that happens at render time.
 * @param {Array} mainData - Main chart data
 * @param {Array} comparisonSymbols - Array of { symbol, data, color } from compareStore
 * @returns {Array} - Main data with all comparison symbols merged
 */
export function mergeAllComparisons(mainData, comparisonSymbols) {
  if (!mainData || mainData.length === 0) return mainData;
  if (!comparisonSymbols || comparisonSymbols.length === 0) return mainData;

  let result = mainData;

  // Merge each comparison symbol's raw close prices
  for (const { symbol, data } of comparisonSymbols) {
    if (data && data.length > 0) {
      result = mergeComparisonSymbol(result, data, symbol);
    }
  }

  return result;
}

// ============================================================================
// Y-AXIS EXTENT FUNCTIONS
// ============================================================================

/**
 * Get Y-axis extent for comparison mode.
 * Returns PRICE extents (not percentages) - the Y-axis domain stays in prices.
 * The tick formatter converts prices to percentages using the baseline.
 * This allows OHLC candles to render at their actual price positions.
 * Comparison symbols are normalized to main symbol's price scale at render time.
 * @param {Array} data - Visible chart data (dataSegment/plotData)
 * @returns {[number, number]} - [min, max] PRICE values for Y-axis
 */
export function getCompareYExtents(data) {
  if (!data || data.length === 0) return [0, 100];

  let min = Infinity;
  let max = -Infinity;

  for (const bar of data) {
    // Check main symbol OHLC (all 4 values for candles)
    for (const field of ["open", "high", "low", "close"]) {
      const value = bar[field];
      if (value !== null && value !== undefined && isFinite(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }
  }

  // Handle edge cases
  if (!isFinite(min) || !isFinite(max)) {
    return [0, 100];
  }

  // Add padding (2% on each side for prices)
  const range = max - min;
  const padding = Math.max(range * 0.02, 0.01);

  return [min - padding, max + padding];
}

/**
 * Transform visible data to include percentage values for compare mode rendering.
 * This is called when the viewport changes to recalculate percentages based on new baseline.
 * @param {Array} visibleData - The visible data segment (from plotData/dataSegment)
 * @param {Array} symbols - Array of { symbol } from compareStore
 * @returns {Object} - { mainBaseline, comparisonBaselines } or null if invalid
 */
export function transformVisibleDataForCompare(visibleData, symbols) {
  if (!visibleData || visibleData.length === 0) return null;

  // Calculate main baseline from first visible bar
  const mainBaseline = getCompareBaseline(visibleData, 0, "close");
  if (!mainBaseline) return null;

  // Calculate comparison baselines
  const comparisonBaselines = {};
  for (const { symbol } of symbols || []) {
    const closeKey = `compare_${symbol}_close`;
    comparisonBaselines[symbol] = getCompareBaseline(visibleData, 0, closeKey);
  }

  return {
    mainBaseline,
    comparisonBaselines,
  };
}

/**
 * Calculate comparison symbol baselines and percentages for visible data.
 * Called when viewport changes to recalculate all percentages.
 * @param {Array} data - Chart data array
 * @param {Array} comparisonSymbols - Array of { symbol } from compareStore
 * @param {number} firstVisibleIndex - Index of first visible bar
 * @returns {Object} - Object with baselines for each symbol
 */
export function calculateCompareBaselines(
  data,
  comparisonSymbols,
  firstVisibleIndex = 0,
) {
  const baselines = {
    main: getCompareBaseline(data, firstVisibleIndex, "close"),
  };

  for (const { symbol } of comparisonSymbols || []) {
    const closeKey = `compare_${symbol}_close`;
    baselines[symbol] = getCompareBaseline(data, firstVisibleIndex, closeKey);
  }

  return baselines;
}

// ============================================================================
// REAL-TIME QUOTE MERGING
// ============================================================================

/**
 * Get the bar time for a quote based on periodicity.
 * Aligns quotes to their appropriate time bucket.
 * @param {Object} quote - Quote object with DT (timestamp in ms)
 * @param {string} periodicity - Chart periodicity (e.g., "1min", "5min", "1D")
 * @returns {Date} - Aligned bar time
 */
function getComparisonBarTime(quote, periodicity) {
  const quoteTime = new Date(quote.DT);

  switch (periodicity) {
    case "1D":
      quoteTime.setHours(18, 50, 0, 0);
      break;
    case "1W": {
      const day = quoteTime.getDay();
      const diff = quoteTime.getDate() - day + (day === 0 ? -6 : 1);
      quoteTime.setDate(diff);
      quoteTime.setHours(0, 0, 0, 0);
      break;
    }
    case "1M":
      quoteTime.setDate(1);
      quoteTime.setHours(0, 0, 0, 0);
      break;
    case "1min":
      quoteTime.setSeconds(0, 0);
      break;
    case "2min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 2) * 2, 0, 0);
      break;
    case "3min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 3) * 3, 0, 0);
      break;
    case "4min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 4) * 4, 0, 0);
      break;
    case "5min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 5) * 5, 0, 0);
      break;
    case "10min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 10) * 10, 0, 0);
      break;
    case "15min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 15) * 15, 0, 0);
      break;
    case "20min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 20) * 20, 0, 0);
      break;
    case "30min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 30) * 30, 0, 0);
      break;
    case "60min":
      quoteTime.setMinutes(0, 0, 0);
      break;
    case "120min": {
      // 2-hour bars: align to 10h market open
      const marketOpenHour = 10;
      const currentHour = quoteTime.getHours();
      const hoursSinceOpen = currentHour - marketOpenHour;
      const barIndex = Math.floor(hoursSinceOpen / 2);
      const alignedHour = marketOpenHour + barIndex * 2;
      quoteTime.setHours(alignedHour, 0, 0, 0);
      break;
    }
    case "240min": {
      // 4-hour bars: align to 10h market open
      const marketOpenHour = 10;
      const currentHour = quoteTime.getHours();
      const hoursSinceOpen = currentHour - marketOpenHour;
      const barIndex = Math.floor(hoursSinceOpen / 4);
      const alignedHour = marketOpenHour + barIndex * 4;
      quoteTime.setHours(alignedHour, 0, 0, 0);
      break;
    }
    default:
      // For unknown periodicities, just use seconds alignment
      quoteTime.setSeconds(0, 0);
  }

  return quoteTime;
}

/**
 * Merge a real-time quote into comparison symbol data in-place.
 * Similar to chartDataProcessor.mergeQuotesInPlace() but for comparison data.
 *
 * @param {Array} data - Comparison symbol's data array (will be mutated)
 * @param {Object} quote - Quote object { S, DT, O, H, L, C, Q, F, TC }
 * @param {string} periodicity - Chart periodicity (e.g., "1min", "5min", "1D")
 * @returns {string|null} - 'updated' | 'added' | null
 */
export function mergeComparisonQuoteInPlace(data, quote, periodicity) {
  if (!data || data.length === 0) return null;

  const barTime = getComparisonBarTime(quote, periodicity);
  const lastBar = data[data.length - 1];

  // Check if quote timestamp matches the last bar (update existing)
  if (lastBar.date.getTime() === barTime.getTime()) {
    // Update existing bar in-place. A traded price is itself a high/low
    // candidate; the server omits H/L on flat ticks (TickHigh==Last), so fold C
    // into high/low so the wick forms regardless of coalescing (marketdata#17).
    // Number.isFinite rejects null/undefined/NaN/+-Infinity (issue #323).
    if (Number.isFinite(quote.C)) {
      lastBar.close = quote.C;
      lastBar.high = Math.max(lastBar.high, quote.C);
      lastBar.low = Math.min(lastBar.low, quote.C);
    }
    if (Number.isFinite(quote.H))
      lastBar.high = Math.max(lastBar.high, quote.H);
    if (Number.isFinite(quote.L)) lastBar.low = Math.min(lastBar.low, quote.L);

    // Accumulate volume (SignalR sends incremental deltas)
    lastBar.volume = (lastBar.volume || 0) + (quote.Q || 0);

    return "updated";
  } else if (barTime.getTime() > lastBar.date.getTime()) {
    // New bar needed - only add if it's after the last bar
    data.push({
      date: barTime,
      open: quote.O !== null ? quote.O : quote.C,
      high: quote.H !== null ? quote.H : quote.C,
      low: quote.L !== null ? quote.L : quote.C,
      close: quote.C,
      volume: quote.Q || 0,
    });
    return "added";
  }

  // Quote is older than last bar, ignore it
  return null;
}

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// (These will be deprecated but kept for gradual migration)
// ============================================================================

/**
 * @deprecated Use priceToPercent with dynamic baseline instead
 */
export function transformToPercentage(data, basePath = "close") {
  if (!data || data.length === 0) return [];

  const firstValue = data[0][basePath];
  if (!firstValue || firstValue === 0) return data;

  return data.map((bar) => ({
    ...bar,
    percentChange: ((bar[basePath] - firstValue) / firstValue) * 100,
  }));
}

/**
 * @deprecated Use priceToPercent with dynamic baseline instead
 */
export function calculatePercentChange(alignedData, basePath = "close") {
  if (!alignedData || alignedData.length === 0) return [];

  let firstValue = null;
  for (const bar of alignedData) {
    if (bar[basePath] !== null && bar[basePath] !== undefined) {
      firstValue = bar[basePath];
      break;
    }
  }

  if (!firstValue || firstValue === 0) return alignedData;

  return alignedData.map((bar) => {
    if (bar[basePath] === null || bar[basePath] === undefined) {
      return { ...bar, percentChange: null };
    }
    return {
      ...bar,
      percentChange: ((bar[basePath] - firstValue) / firstValue) * 100,
    };
  });
}

/**
 * @deprecated Use getCompareBaseline with dynamic first visible index instead
 */
export function calculateMainPercentChange(mainData, basePath = "close") {
  if (!mainData || mainData.length === 0) return mainData;

  let firstValue = null;
  for (const bar of mainData) {
    if (bar[basePath] !== null && bar[basePath] !== undefined) {
      firstValue = bar[basePath];
      break;
    }
  }

  if (!firstValue || firstValue === 0) return mainData;

  return mainData.map((bar) => {
    const value = bar[basePath];
    if (value === null || value === undefined) {
      return { ...bar, main_percentChange: null };
    }
    return {
      ...bar,
      main_percentChange: ((value - firstValue) / firstValue) * 100,
    };
  });
}
